import { z } from "zod";
import { BOOK_FORMATS } from "./formats";
import type { ReadingStatus } from "./reading";
import { canonicalLanguageTag, isIsbn10Valid, isIsbn13Valid, isValidPublishedDate } from "./text";

/*
 * Catalog contract shared by server and UI: query/body schemas (issue messages are
 * field error codes translated by the client) and the DTOs of the catalog API.
 */

export const BOOK_PAGE_SIZE_DEFAULT = 48;
export const BOOK_PAGE_SIZE_MAX = 100;
export const ENTITY_PAGE_SIZE_DEFAULT = 100;
export const ENTITY_PAGE_SIZE_MAX = 200;
export const MAX_SEARCH_LENGTH = 200;
export const MAX_TITLE_LENGTH = 500;
export const MAX_NAME_LENGTH = 200;
export const MAX_DESCRIPTION_LENGTH = 100_000;
export const MAX_AUTHORS = 50;
export const MAX_TAGS = 100;
export const MAX_SERIES_INDEX = 99_999;

export const BOOK_SORTS = [
  "added",
  "title",
  "author",
  "series",
  "published",
  "relevance",
  "rating",
] as const;
export type BookSort = (typeof BOOK_SORTS)[number];
export const SORT_ORDERS = ["asc", "desc"] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

export const CATALOG_ENTITY_KINDS = ["authors", "series", "tags", "publishers"] as const;
export type CatalogEntityKind = (typeof CATALOG_ENTITY_KINDS)[number];

export const entityId = z.string({ error: "invalid" }).min(1, { error: "invalid" }).max(64, {
  error: "invalid",
});

export const MAX_FILTER_VALUES = 20;

/** A filter that takes one value or several (repeated query parameter); yields a de-duplicated array. */
export const multi = <T extends z.ZodType<string>>(item: T) =>
  z
    .union([item, z.array(item)])
    .transform((value): z.infer<T>[] => [...new Set(Array.isArray(value) ? value : [value])])
    .pipe(
      z.array(item).max(MAX_FILTER_VALUES, { error: "too_many" }) as z.ZodType<
        z.infer<T>[],
        z.infer<T>[]
      >,
    );

export const BOOK_FILES_FILTERS = ["with", "without"] as const;
export type BookFilesFilter = (typeof BOOK_FILES_FILTERS)[number];

/** Query of `GET /api/books`. Later changes extend it with `.extend({...})` (filters and sorts). */
export const bookListQuerySchema = z.object({
  page: z.coerce
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(1, { error: "out_of_range" })
    .max(1_000_000, { error: "out_of_range" })
    .default(1),
  pageSize: z.coerce
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(1, { error: "out_of_range" })
    .max(BOOK_PAGE_SIZE_MAX, { error: "out_of_range" })
    .default(BOOK_PAGE_SIZE_DEFAULT),
  sort: z.enum(BOOK_SORTS, { error: "invalid" }).optional(),
  order: z.enum(SORT_ORDERS, { error: "invalid" }).optional(),
  q: z.string({ error: "invalid" }).max(MAX_SEARCH_LENGTH, { error: "too_long" }).optional(),
  author: multi(entityId).optional(),
  series: multi(entityId).optional(),
  tag: multi(entityId).optional(),
  publisher: multi(entityId).optional(),
  language: multi(z.string({ error: "invalid" }).min(1).max(35, { error: "invalid" })).optional(),
  format: multi(z.enum(BOOK_FORMATS, { error: "invalid" })).optional(),
  /** `with`: books with at least one file; `without`: books without files (mark-suggestion-read). */
  files: z.enum(BOOK_FILES_FILTERS, { error: "invalid" }).optional(),
});
export type BookListQuery = z.infer<typeof bookListQuerySchema>;

export const ENTITY_SORTS = ["name", "count"] as const;

/** Query of `GET /api/{authors,series,tags,publishers}`. */
export const entityListQuerySchema = z.object({
  page: bookListQuerySchema.shape.page,
  pageSize: z.coerce
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(1, { error: "out_of_range" })
    .max(ENTITY_PAGE_SIZE_MAX, { error: "out_of_range" })
    .default(ENTITY_PAGE_SIZE_DEFAULT),
  q: z.string({ error: "invalid" }).max(MAX_SEARCH_LENGTH, { error: "too_long" }).optional(),
  sort: z.enum(ENTITY_SORTS, { error: "invalid" }).default("name"),
});
export type EntityListQuery = z.infer<typeof entityListQuerySchema>;

// --- book update ---------------------------------------------------------------

const optionalText = (max: number) =>
  z
    .string({ error: "invalid" })
    .trim()
    .max(max, { error: "too_long" })
    .nullish()
    .transform((value) => (value ? value : null));

const nameText = z
  .string({ error: "required" })
  .trim()
  .min(1, { error: "required" })
  .max(MAX_NAME_LENGTH, { error: "too_long" });

/** Case-insensitive de-duplication that keeps the first spelling and the order. */
function dedupeNames(names: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const name of names) {
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(name);
  }
  return result;
}

export const identifierSchema = z
  .object({
    type: z
      .string({ error: "required" })
      .trim()
      .regex(/^[a-z0-9_]{1,32}$/, { error: "invalid" }),
    value: z
      .string({ error: "required" })
      .trim()
      .min(1, { error: "required" })
      .max(MAX_NAME_LENGTH, { error: "too_long" }),
  })
  .transform((identifier, ctx) => {
    if (identifier.type !== "isbn" && identifier.type !== "isbn13") return identifier;
    const value = identifier.value.replace(/[\s-]/g, "").toUpperCase();
    const valid =
      identifier.type === "isbn13"
        ? isIsbn13Valid(value)
        : isIsbn10Valid(value) || isIsbn13Valid(value);
    if (!valid) {
      ctx.issues.push({
        code: "custom",
        message: "invalid_isbn",
        input: identifier,
        path: ["value"],
      });
      return z.NEVER;
    }
    return { ...identifier, value };
  });

export const bookUpdateSchema = z
  .object({
    title: z
      .string({ error: "required" })
      .trim()
      .min(1, { error: "required" })
      .max(MAX_TITLE_LENGTH, { error: "too_long" }),
    sortTitle: optionalText(MAX_TITLE_LENGTH),
    subtitle: optionalText(MAX_TITLE_LENGTH),
    description: z
      .string({ error: "invalid" })
      .max(MAX_DESCRIPTION_LENGTH, { error: "too_long" })
      .nullish()
      .transform((value) => (value?.trim() ? value : null)),
    language: optionalText(35)
      .refine((value) => value === null || canonicalLanguageTag(value) !== undefined, {
        error: "invalid_language",
      })
      .transform((value) => (value === null ? null : (canonicalLanguageTag(value) ?? value))),
    publishedDate: optionalText(10).refine(
      (value) => value === null || isValidPublishedDate(value),
      {
        error: "invalid_date",
      },
    ),
    publisher: optionalText(MAX_NAME_LENGTH),
    series: optionalText(MAX_NAME_LENGTH),
    seriesIndex: z
      .number({ error: "invalid" })
      .min(0, { error: "out_of_range" })
      .max(MAX_SERIES_INDEX, { error: "out_of_range" })
      .refine((value) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-6, {
        error: "invalid",
      })
      .nullish()
      .transform((value) => value ?? null),
    authors: z
      .array(nameText, { error: "invalid" })
      .max(MAX_AUTHORS, { error: "too_many" })
      .transform(dedupeNames),
    tags: z
      .array(nameText, { error: "invalid" })
      .max(MAX_TAGS, { error: "too_many" })
      .transform(dedupeNames),
    identifiers: z.array(identifierSchema, { error: "invalid" }).max(50, { error: "too_many" }),
    /** The `updatedAt` value of the book as loaded by the editor (optimistic concurrency). */
    updatedAt: z.iso.datetime({ error: "invalid" }),
  })
  .superRefine((value, ctx) => {
    if (value.seriesIndex !== null && value.series === null) {
      ctx.addIssue({ code: "custom", message: "series_required", path: ["seriesIndex"] });
    }
    const seen = new Set<string>();
    value.identifiers.forEach((identifier, index) => {
      if (seen.has(identifier.type)) {
        ctx.addIssue({
          code: "custom",
          message: "duplicate_identifier_type",
          path: ["identifiers", index, "type"],
        });
      }
      seen.add(identifier.type);
    });
  });
export type BookUpdateInput = z.infer<typeof bookUpdateSchema>;
export type BookUpdateFormInput = z.input<typeof bookUpdateSchema>;

// --- rename / merge ------------------------------------------------------------

export const entityRenameSchema = z.object({
  name: nameText,
  /** Authors and series only; omitted keeps the current sort name. */
  sortName: z
    .string({ error: "invalid" })
    .trim()
    .min(1, { error: "required" })
    .max(MAX_NAME_LENGTH, { error: "too_long" })
    .optional(),
});
export type EntityRenameInput = z.infer<typeof entityRenameSchema>;

export const entityMergeSchema = z.object({
  targetId: z.string({ error: "required" }).min(1, { error: "required" }).max(64, {
    error: "invalid",
  }),
});
export type EntityMergeInput = z.infer<typeof entityMergeSchema>;

// --- DTOs ----------------------------------------------------------------------

export interface AuthorRefDto {
  id: string;
  name: string;
}

export interface BookListItemDto {
  id: string;
  title: string;
  subtitle: string | null;
  authors: AuthorRefDto[];
  series: { id: string; name: string } | null;
  seriesIndex: number | null;
  publishedDate: string | null;
  language: string | null;
  /** Distinct file formats of the book; empty means "no files". */
  formats: string[];
  hasCover: boolean;
  /** Cover version token (epoch ms); part of the cover URL for cache busting. */
  coverVersion: number | null;
  /** The requesting user's reading status (add-reading-tracking). */
  myStatus?: ReadingStatus;
  /** Average rating (one decimal) and number of ratings (add-ratings); `null` when unrated. */
  avgRating?: number | null;
  ratingCount?: number;
  /** The requesting user's own stars; `null` when they have not rated the book. */
  myRating?: number | null;
}

export interface BookListDto {
  books: BookListItemDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BookFileDto {
  id: string;
  format: string;
  sizeBytes: number;
  originalFilename: string;
  createdAt: string;
}

export const BOOK_ORIGINS = ["suggestion"] as const;
export type BookOrigin = (typeof BOOK_ORIGINS)[number];

export interface BookDetailDto {
  id: string;
  title: string;
  sortTitle: string;
  subtitle: string | null;
  /** Sanitized HTML. */
  description: string | null;
  language: string | null;
  publishedDate: string | null;
  authors: { id: string; name: string; sortName: string }[];
  series: { id: string; name: string } | null;
  seriesIndex: number | null;
  tags: { id: string; name: string }[];
  publisher: { id: string; name: string } | null;
  identifiers: { type: string; value: string }[];
  files: BookFileDto[];
  hasCover: boolean;
  coverVersion: number | null;
  createdAt: string;
  /** ISO timestamp with milliseconds; send it back as `updatedAt` when saving. */
  updatedAt: string;
  /** `null` for books created from a suggestion (`origin: "suggestion"`): the creator stays private. */
  addedBy: { name: string } | null;
  /** `"suggestion"`: created by marking a suggestion as read (mark-suggestion-read); `null` otherwise. */
  origin: BookOrigin | null;
  /** The requesting user's reading status (add-reading-tracking). */
  myStatus?: ReadingStatus;
  /** Average rating (one decimal) and number of ratings (add-ratings); `null` when unrated. */
  avgRating?: number | null;
  ratingCount?: number;
  /** The requesting user's own stars; `null` when they have not rated the book. */
  myRating?: number | null;
}

export interface CatalogFacetsDto {
  languages: { value: string; count: number }[];
  formats: { value: string; count: number }[];
  /** Books with and without files (the "Files" filter). */
  files: { with: number; without: number };
}

export interface CatalogEntityDto {
  id: string;
  name: string;
  /** Authors and series only. */
  sortName?: string;
  bookCount: number;
}

export interface CatalogEntityListDto {
  items: CatalogEntityDto[];
  total: number;
  page: number;
  pageSize: number;
}
